/**
 * Ring crossings, relay-transit gating and lane exits (plan R6, §2.9 flows 6–7; spec §4.1, §4.3). Allocation-free.
 * Positions are float64 system-frame metres; a crossing is found on the tick's straight segment, so even a
 * 1e10 m/s cruise step cannot tunnel through a ring.
 */
import type { TravelConfig } from '../config'

const DEG = Math.PI / 180

export interface DiscCrossingOut {
  /** +1 when moving along the disc normal, −1 against it. */
  sign: number
  /** The crossing point on the disc plane. */
  x: number
  y: number
  z: number
}

export function createDiscCrossing(): DiscCrossingOut {
  return { sign: 0, x: 0, y: 0, z: 0 }
}

/**
 * Whether the segment prev → cur crosses the plane of the disc (centre c, normal n, any length) inside `radius`.
 * A segment ending exactly on the plane counts; one starting on it does not (so a crossing is never counted twice),
 * and a segment lying in the plane is no crossing. Writes the sign and the crossing point into `out` on success.
 */
export function discCrossing(
  prevX: number,
  prevY: number,
  prevZ: number,
  curX: number,
  curY: number,
  curZ: number,
  cX: number,
  cY: number,
  cZ: number,
  nX: number,
  nY: number,
  nZ: number,
  radius: number,
  out: DiscCrossingOut,
): boolean {
  const sPrev = (prevX - cX) * nX + (prevY - cY) * nY + (prevZ - cZ) * nZ
  const sCur = (curX - cX) * nX + (curY - cY) * nY + (curZ - cZ) * nZ
  const crosses = (sPrev < 0 && sCur >= 0) || (sPrev > 0 && sCur <= 0)
  if (!crosses) return false
  const t = sPrev / (sPrev - sCur)
  const x = prevX + (curX - prevX) * t
  const y = prevY + (curY - prevY) * t
  const z = prevZ + (curZ - prevZ) * t
  const dx = x - cX
  const dy = y - cY
  const dz = z - cZ
  if (dx * dx + dy * dy + dz * dz > radius * radius) return false
  out.sign = sCur > sPrev ? 1 : -1
  out.x = x
  out.y = y
  out.z = z
  return true
}

export interface TransitGateOut {
  /** The nose is within `alignDeg` of the transit direction. */
  aligned: boolean
  /** The speed is below `maxSpeed`. */
  slowEnough: boolean
}

export function createTransitGate(): TransitGateOut {
  return { aligned: false, slowEnough: false }
}

/** The relay-transit checks (spec §4.1): nose within `alignDeg` of the outward direction, speed below `maxSpeed`. */
export function transitGate(
  noseX: number,
  noseY: number,
  noseZ: number,
  speed: number,
  outX: number,
  outY: number,
  outZ: number,
  cfg: TravelConfig,
  out: TransitGateOut,
): void {
  const t = cfg.transit
  const nl = Math.sqrt(noseX * noseX + noseY * noseY + noseZ * noseZ)
  const ol = Math.sqrt(outX * outX + outY * outY + outZ * outZ)
  const dot = noseX * outX + noseY * outY + noseZ * outZ
  out.aligned = nl > 0 && ol > 0 && dot >= nl * ol * Math.cos(t.alignDeg * DEG)
  out.slowEnough = speed < t.maxSpeed
}

/**
 * The lane ring (by `order`) a ride entered at `entryOrder` ends at: the last ring in the direction of travel
 * (`direction` +1 toward `to`, −1 toward `from`) before any down ring. −1 when the next ring is down or there is
 * no next ring. `up[order]` is each ring's state along the lane.
 */
export function laneExitOrder(up: ArrayLike<boolean>, entryOrder: number, direction: number): number {
  const step = direction < 0 ? -1 : 1
  let exit = -1
  for (let i = entryOrder + step; i >= 0 && i < up.length; i += step) {
    if (!up[i]) break
    exit = i
  }
  return exit
}
